import React, { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Modal,
  Form,
  Table,
  Container,
  Row,
  Col,
  Card,
  ButtonGroup,
  Badge,
  Spinner,
  InputGroup,
  ProgressBar,
  Collapse,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap'
import Navbar from '../../components/navbar/navbar'
import apinode from '../../services/apinode'
import api from '../../services/api'
import swal from 'sweetalert'
import {
  Search,
  Printer,
  CheckCircle2,
  Trash2,
  Package,
  FileText,
  Loader2,
  Plus,
  Minus,
  RefreshCw,
  Eye,
  EyeOff,
  Info,
  CircleCheck,
  CircleDashed,
  X,
  RotateCw,
} from 'lucide-react'
import generateReceiptPDF from './report.jsx'
import './retiradas.css'

/**
 * WithdrawalScreen (refatorado v2 + pendente por item)
 * - Mostra “pendente” (o que foi adicionado antes de confirmar) por item
 * - Saldo disponível = saldoOriginal - pendente
 * - Badge +X pend. na tabela, modal exibe números e trava se zerar
 */
export default function WithdrawalScreen() {
  // ======= Estados base
  const [showModal, setShowModal] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [qty, setQty] = useState(1)
  const [obs, setObs] = useState('')

  const [itensPedido, setItensPedido] = useState([])
  const [withdrawnItems, setWithdrawnItems] = useState([])
  const [dadosCliente, setDadosCliente] = useState({})
  const [dadosEmp, setDadosEmp] = useState([])
  const [logsMov, setLogsMov] = useState([])

  const [searchTerm, setSearchTerm] = useState(localStorage.getItem('lastPedido') || '')
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [showLogs, setShowLogs] = useState(false)

  // ======= Helpers (saldo original do item segundo o backend)
  const saldoOf = (item) =>
    (Number(item?.QUANTIDADE_NOVA) || 0) - (Number(item?.QUANTIDADE_RETIRADA) || 0)

  // Quantidade já selecionada (pendente) para um item
  const selectedQtyOf = (itemOrId) => {
    const id = typeof itemOrId === 'object' ? itemOrId.ID : itemOrId
    const found = withdrawnItems.find((w) => w.ID === id)
    return Number(found?.QUANTIDADE_RETIRADA) || 0
  }

  // Saldo disponível considerando o que já foi selecionado (pendente)
  const saldoDisponivelOf = (item) => {
    const saldo = saldoOf(item)
    const pendente = selectedQtyOf(item)
    return Math.max(0, saldo - pendente)
  }

  const handleOpenModal = (item) => {
    setSelectedItem(item)
    const disp = saldoDisponivelOf(item)
    setQty(disp > 0 ? 1 : 0)
    setObs('')
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSelectedItem(null)
    setQty(1)
    setObs('')
  }

  const changeQty = (delta) => {
    setQty((old) => Math.max(0, (Number(old) || 0) + delta))
  }

  const addWithdraw = () => {
    if (!selectedItem) return
    const saldoDisp = saldoDisponivelOf(selectedItem)
    const q = Number(qty) || 0

    if (q <= 0) {
      swal('Quantidade inválida', 'Informe uma quantidade maior que zero.', 'warning')
      return
    }
    if (q > saldoDisp) {
      swal('Excede o saldo', `Quantidade máxima disponível: ${saldoDisp}.`, 'warning')
      return
    }

    setWithdrawnItems((prev) => {
      const idx = prev.findIndex((p) => p.ID === selectedItem.ID)
      if (idx >= 0) {
        const clone = [...prev]
        const novoTotal = (Number(clone[idx].QUANTIDADE_RETIRADA) || 0) + q
        // Garantia contra ultrapassar o saldo original vindo do backend
        if (novoTotal > saldoOf(selectedItem)) {
          swal('Excede o saldo', 'Soma das retiradas ultrapassa o saldo.', 'warning')
          return prev
        }
        clone[idx] = {
          ...clone[idx],
          QUANTIDADE_RETIRADA: novoTotal,
          OBSERVACOES: obs,
        }
        return clone
      }
      return [
        ...prev,
        {
          ...selectedItem,
          QUANTIDADE_RETIRADA: q,
          OBSERVACOES: obs,
        },
      ]
    })

    handleCloseModal()
  }

  const removeWithdraw = (id) => {
    setWithdrawnItems((prev) => prev.filter((w) => w.ID !== id))
  }

  const clearSelection = () => {
    if (!withdrawnItems.length) return
    setWithdrawnItems([])
  }

  // ======= API
  const reqToken = async (conta) => {
    try {
      const r = await api.get('/emp', { params: { token: conta } })
      if (r.status === 200) setDadosEmp(r.data)
    } catch (e) {
      console.error(e)
      swal('Erro', 'Falha ao obter dados da empresa.', 'error')
    }
  }

  const fetchPedidoItems = async (pedido) => {
    if (!pedido) return
    setLoading(true)
    try {
      const r = await apinode.get(
        `/itensdevolucao/itensretirada/${pedido}/${localStorage.idconta}`,
      )
      if (r?.data?.itensPedido?.length) {
        setItensPedido(r.data.itensPedido)
        setDadosCliente(r.data.cliente || {})
        setWithdrawnItems([]) // reset fluxo
        localStorage.setItem('lastPedido', pedido)
      } else {
        setItensPedido([])
        setDadosCliente({})
        swal('Atenção', 'Nenhum item encontrado para este pedido.', 'info')
      }
    } catch (e) {
      console.error(e)
      swal('Erro', 'Falha ao buscar itens do pedido.', 'error')
    } finally {
      setLoading(false)
    }
  }

  const fetchLogs = async (pedido) => {
    if (!pedido) return
    try {
      const r = await apinode.get(
        `/itensdevolucao/logmov/${pedido}/${localStorage.idconta}`,
      )
      setLogsMov(r?.data || [])
    } catch (e) {
      console.error(e)
    }
  }

  const handleSearch = async () => {
    if (!searchTerm?.trim()) {
      swal('Atenção', 'Digite o número do pedido para buscar.', 'info')
      return
    }
    await Promise.all([fetchPedidoItems(searchTerm.trim()), fetchLogs(searchTerm.trim())])
  }

  const handleConfirmWithdraw = async () => {
    if (!dadosCliente?.PEDIDO) {
      swal('Pedido inválido', 'Busque um pedido antes de confirmar.', 'warning')
      return
    }
    if (!withdrawnItems.length) {
      swal('Nada para confirmar', 'Adicione pelo menos um item retirado.', 'info')
      return
    }

    setSubmitting(true)
    try {
      await apinode.post(
        `/itensdevolucao/itempedido/${dadosCliente.PEDIDO}/${localStorage.idconta}`,
        withdrawnItems,
      )

      const imprimir = await swal({
        title: 'Imprimir comprovante?',
        text: 'Deseja gerar o PDF agora?',
        icon: 'info',
        buttons: ['Não', 'Sim'],
      })

      if (imprimir) {
        try {
          generateReceiptPDF(
            withdrawnItems,
            dadosCliente.CLIENTE,
            dadosCliente.PEDIDO,
            dadosEmp,
          )
          swal('Sucesso', 'Comprovante gerado com sucesso!', 'success')
        } catch (e) {
          console.error(e)
          swal('Erro', 'Falha ao gerar comprovante.', 'error')
        }
      } else {
        swal('Sucesso', 'Retiradas registradas com sucesso!', 'success')
      }

      setWithdrawnItems([])
      await Promise.all([fetchPedidoItems(searchTerm), fetchLogs(searchTerm)])
    } catch (e) {
      console.error(e)
      const msg = e?.response?.data?.message || 'Falha ao confirmar retirada.'
      swal('Erro', msg, 'error')
    } finally {
      setSubmitting(false)
    }
  }

  // ======= Reimprimir a partir de um log
  const reprintFromLog = async (log) => {
    if (!dadosCliente?.PEDIDO) {
      swal('Pedido inválido', 'Busque um pedido antes de reimprimir.', 'warning')
      return
    }

    try {
      const fakeItem = {
        ID: log.ID,
        CODIGO: log.CODIGO,
        DESCRICAO: log.DESCRICAO,
        QUANTIDADE_RETIRADA: Number(log.QTD_MOV || 0),
        OBSERVACOES: log.OBS || '',
      }
      generateReceiptPDF([fakeItem], dadosCliente.CLIENTE, dadosCliente.PEDIDO, dadosEmp)
      swal('OK', 'Comprovante reimresso para o movimento selecionado.', 'success')
    } catch (e) {
      console.error(e)
      swal('Erro', 'Não foi possível reimprimir este movimento.', 'error')
    }
  }

  // ======= Efeitos
  useEffect(() => {
    reqToken(localStorage.idconta)
  }, [])

  // ======= Derivados / Métricas de progresso
  const resumo = useMemo(() => {
    const totalItens = itensPedido.length
    const totalPedido = itensPedido.reduce(
      (acc, it) => acc + (Number(it.QUANTIDADE_NOVA) || 0),
      0,
    )
    const totalRetirado = itensPedido.reduce(
      (acc, it) => acc + (Number(it.QUANTIDADE_RETIRADA) || 0),
      0,
    )
    const totalSaldo = itensPedido.reduce((acc, it) => acc + saldoOf(it), 0)
    const totalSelecionado = withdrawnItems.reduce(
      (acc, it) => acc + (Number(it.QUANTIDADE_RETIRADA) || 0),
      0,
    )
    const percentConcluido =
      totalPedido > 0 ? Math.min(100, Math.round((totalRetirado / totalPedido) * 100)) : 0
    return {
      totalItens,
      totalPedido,
      totalRetirado,
      totalSaldo,
      totalSelecionado,
      percentConcluido,
    }
  }, [itensPedido, withdrawnItems])

  const allComplete = resumo.totalSaldo === 0 && resumo.totalPedido > 0

  // ======= UI auxiliares
  const StatusBadge = ({ saldoDisp }) => {
    if (saldoDisp === 0) return <Badge bg="success">Completo</Badge>
    if (saldoDisp > 0) return <Badge bg="warning" text="dark">Pendente</Badge>
    return <Badge bg="danger">Erro</Badge>
  }

  const RowClass = (saldoDisp) => {
    if (saldoDisp === 0) return 'table-success'
    if (saldoDisp < 0) return 'table-danger'
    return ''
  }

  const StepBadge = ({ active, done, label }) => {
    let icon = <CircleDashed size={16} className="me-1" />
    let bg = 'secondary'
    if (done) {
      icon = <CircleCheck size={16} className="me-1" />
      bg = 'success'
    } else if (active) {
      icon = <Info size={16} className="me-1" />
      bg = 'primary'
    }
    return (
      <Badge bg={bg} className="me-2 d-inline-flex align-items-center">
        {icon}
        {label}
      </Badge>
    )
  }

  const Hint = ({ text }) => (
    <OverlayTrigger placement="top" overlay={<Tooltip>{text}</Tooltip>}>
      <Info size={16} className="ms-2 text-muted" />
    </OverlayTrigger>
  )

  return (
    <>
      <Navbar />
      <div className="mt-page">
        <Container fluid>
          {/* Header / Passos */}
          <Row className="align-items-center mb-3">
            <Col md="6">
              <h1 className="text-start custom-header d-flex align-items-center gap-2" style={{ gap: 8 }}>
                <Package size={26} /> Retiradas de Itens
              </h1>
              <div className="mt-2">
                <StepBadge
                  active={!dadosCliente?.PEDIDO}
                  done={!!dadosCliente?.PEDIDO}
                  label="1) Buscar pedido"
                />
                <StepBadge
                  active={!!dadosCliente?.PEDIDO && withdrawnItems.length === 0}
                  done={withdrawnItems.length > 0}
                  label="2) Selecionar retiradas"
                />
                <StepBadge
                  active={withdrawnItems.length > 0}
                  done={allComplete}
                  label="3) Confirmar / Imprimir"
                />
              </div>
            </Col>
            <Col md="6" className="text-md-end mt-3 mt-md-0">
              <ButtonGroup>
                <Button
                  variant="outline-secondary"
                  onClick={() => {
                    setSearchTerm('')
                    setItensPedido([])
                    setDadosCliente({})
                    setWithdrawnItems([])
                    setLogsMov([])
                  }}
                >
                  <X size={18} className="me-1" /> Limpar
                </Button>

                <Button
                  variant="outline-primary"
                  onClick={handleSearch}
                  disabled={!searchTerm || loading}
                >
                  {loading ? <Loader2 size={18} className="me-1" /> : <Search size={18} className="me-1" />} Buscar
                </Button>

                <Button
                  variant="outline-info"
                  onClick={() => {
                    if (!dadosCliente?.PEDIDO) return swal('Atenção', 'Busque um pedido primeiro.', 'info')
                    fetchPedidoItems(dadosCliente.PEDIDO)
                  }}
                  disabled={!dadosCliente?.PEDIDO || loading}
                >
                  <RotateCw size={18} className="me-1" /> Recarregar
                </Button>

                <Button
                  variant="outline-success"
                  onClick={() => {
                    if (!withdrawnItems.length) return swal('Nada para imprimir', 'Adicione itens retirados primeiro.', 'info')
                    try {
                      generateReceiptPDF(withdrawnItems, dadosCliente.CLIENTE, dadosCliente.PEDIDO, dadosEmp)
                    } catch (e) {
                      console.error(e)
                      swal('Erro', 'Falha ao gerar comprovante.', 'error')
                    }
                  }}
                  disabled={withdrawnItems.length === 0}
                >
                  <Printer size={18} className="me-1" /> Imprimir
                </Button>

                <Button
                  variant="success"
                  onClick={handleConfirmWithdraw}
                  disabled={submitting || withdrawnItems.length === 0}
                >
                  {submitting ? <Loader2 size={18} className="me-1" /> : <CheckCircle2 size={18} className="me-1" />} Confirmar
                </Button>
              </ButtonGroup>
            </Col>
          </Row>

          {/* Busca + cabeçalho do pedido */}
          <Card className="mb-3">
            <Card.Body>
              <Row className="g-2 align-items-center">
                <Col md={7} lg={6}>
                  <InputGroup>
                    <InputGroup.Text><Search size={16} /></InputGroup.Text>
                    <Form.Control
                      placeholder="Digite o número do pedido e pressione Enter"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <Button variant="primary" onClick={handleSearch} disabled={!searchTerm || loading}>
                      Buscar
                    </Button>
                  </InputGroup>
                </Col>

                <Col md="auto">
                  <div style={{ opacity: dadosCliente?.PEDIDO ? 1 : 0.6 }}>
                    <FileText size={16} className="me-1" />
                    <strong>Pedido:</strong> {dadosCliente?.PEDIDO || '—'}
                  </div>
                </Col>
                <Col md="auto">
                  <div style={{ opacity: dadosCliente?.CLIENTE ? 1 : 0.6 }}>
                    <strong>Cliente:</strong> {dadosCliente?.CLIENTE || '—'}
                  </div>
                </Col>
                <Col md="auto">
                  <div style={{ opacity: dadosCliente?.DATA ? 1 : 0.6 }}>
                    <strong>Data:</strong>{' '}
                    {dadosCliente?.DATA ? new Date(dadosCliente.DATA).toLocaleDateString() : '—'}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Resumo */}
          <Row className="mb-3 g-3">
            <Col md={3}>
              <Card className="h-100">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <div className="text-muted">Itens no pedido</div>
                      <div className="fs-4 fw-semibold">{resumo.totalItens}</div>
                    </div>
                    <Badge bg="secondary">Lista</Badge>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="h-100">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <div className="text-muted">Qtd. pedida</div>
                      <div className="fs-4 fw-semibold">{resumo.totalPedido}</div>
                    </div>
                    <Badge bg="dark">Pedido</Badge>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="h-100">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <div className="text-muted">Qtd. já retirada</div>
                      <div className="fs-4 fw-semibold">{resumo.totalRetirado}</div>
                    </div>
                    <Badge bg="success">Retirada</Badge>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="h-100">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <div className="text-muted">Saldo geral</div>
                      <div className="fs-4 fw-semibold">
                        {resumo.totalSaldo}
                        <Hint text="Soma do que falta retirar em todos os itens." />
                      </div>
                    </div>
                    <Badge bg={resumo.totalSaldo === 0 && resumo.totalPedido > 0 ? 'success' : 'warning'} text={resumo.totalSaldo === 0 ? undefined : 'dark'}>
                      {resumo.totalSaldo === 0 && resumo.totalPedido > 0 ? 'Completo' : 'Pendente'}
                    </Badge>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Progresso do Pedido */}
          <Card className="mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between mb-2">
                <div className="text-muted">Progresso do pedido</div>
                <div className="text-muted">{resumo.percentConcluido}%</div>
              </div>
              <ProgressBar now={resumo.percentConcluido} animated={resumo.percentConcluido > 0 && resumo.percentConcluido < 100} />
            </Card.Body>
          </Card>

          {/* Tabela de Itens */}
          <Card className="mb-4">
            <Card.Header className="d-flex align-items-center justify-content-between">
              <span className="fw-semibold d-flex align-items-center">
                <Package size={18} className="me-2" />
                Itens do Pedido
              </span>
              <div className="d-flex align-items-center gap-2">
                {loading && <Spinner animation="border" size="sm" />}
                <small className="text-muted d-none d-md-inline">
                  Clique em “Retirar” para lançar uma retirada.
                </small>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table striped bordered hover className="mb-0 items-table">
                  <thead className="custom-table-header">
                    <tr>
                      <th>SKU</th>
                      <th>Descrição</th>
                      <th>Qtd. Pedido</th>
                      <th>Qtd. Retirada</th>
                      <th>Saldo disp.</th>
                      <th>Status</th>
                      <th style={{ width: 180 }}>Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itensPedido.map((it) => {
                      const pendente = selectedQtyOf(it)
                      const saldoDisp = saldoDisponivelOf(it)

                      return (
                        <tr key={it.ID} className={RowClass(saldoDisp)}>
                          <td>{it.CODIGO}</td>
                          <td className="text-truncate" title={it.DESCRICAO}>{it.DESCRICAO}</td>
                          <td>{Number(it.QUANTIDADE_NOVA || 0)}</td>
                          <td>
                            {Number(it.QUANTIDADE_RETIRADA || 0)}
                            {pendente > 0 && (
                              <Badge bg="info" className="ms-2">+{pendente} pend.</Badge>
                            )}
                          </td>
                          <td>{saldoDisp}</td>
                          <td><StatusBadge saldoDisp={saldoDisp} /></td>
                          <td className="text-center">
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip>
                                {saldoDisp > 0 ? 'Lançar nova retirada' : 'Sem saldo disponível'}
                              </Tooltip>}
                            >
                              <span>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => handleOpenModal(it)}
                                  disabled={saldoDisp <= 0}
                                >
                                  Retirar
                                </Button>
                              </span>
                            </OverlayTrigger>
                          </td>
                        </tr>
                      )
                    })}
                    {(!itensPedido || itensPedido.length === 0) && (
                      <tr>
                        <td colSpan={7} className="text-center text-muted py-4">Nenhum item carregado.</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>

          {/* Itens Retirados (pré-confirmação) */}
          <Card className="mb-4">
            <Card.Header className="d-flex align-items-center justify-content-between">
              <span className="fw-semibold">Itens Retirados (a confirmar)</span>
              <div className="d-flex align-items-center gap-2">
                <Badge bg="primary">Total selecionado: {resumo.totalSelecionado}</Badge>
                <Button variant="outline-danger" size="sm" onClick={clearSelection} disabled={withdrawnItems.length === 0}>
                  <Trash2 size={16} className="me-1" /> Limpar seleção
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table striped bordered hover className="mb-0 items-table">
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Descrição</th>
                      <th>Quantidade</th>
                      <th>Observações</th>
                      <th style={{ width: 90 }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {withdrawnItems.map((w) => (
                      <tr key={w.ID}>
                        <td>{w.CODIGO}</td>
                        <td className="text-truncate" title={w.DESCRICAO}>{w.DESCRICAO}</td>
                        <td>{w.QUANTIDADE_RETIRADA}</td>
                        <td>{w.OBSERVACOES || '—'}</td>
                        <td className="text-center">
                          <Button variant="outline-danger" size="sm" onClick={() => removeWithdraw(w.ID)} title="Remover">
                            <Trash2 size={16} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {withdrawnItems.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-center text-muted py-4">Nenhum item retirado ainda.</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>

          {/* Logs de Movimentação (escondidos por padrão) */}
          <Card className="mb-5">
            <Card.Header className="d-flex align-items-center justify-content-between">
              <span className="d-inline-flex align-items-center gap-2">
                <RefreshCw size={16} />
                Log de Movimentação
                <Hint text="Clique para expandir. Ao clicar em um log, reimprime o comprovante daquele movimento." />
              </span>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowLogs((s) => !s)}
                disabled={!logsMov?.length}
              >
                {showLogs ? <><EyeOff size={16} className="me-1" />Esconder</> : <><Eye size={16} className="me-1" />Mostrar</>}
              </Button>
            </Card.Header>

            <Collapse in={showLogs}>
              <div>
                <Card.Body>
                  <div className="card-container">
                    {logsMov.map((item) => (
                      <button
                        key={item.ID}
                        className="card mb-2 text-start w-100"
                        style={{ cursor: 'pointer' }}
                        onClick={() => reprintFromLog(item)}
                        title="Clique para reimprimir este movimento"
                      >
                        <div className="card-body">
                          <p className="card-text mb-1">
                            <strong>{Number(item.QTD_MOV || 0).toFixed(2)} x</strong>{' '}
                            <span className="red-text">{item.CODIGO}</span>{' '}
                            <span className="card-item-desc">{item.DESCRICAO}</span>
                          </p>
                          {item.OBS && (
                            <p className="card-text mb-1">
                              <strong>Observações:</strong> {item.OBS}
                            </p>
                          )}
                          <div className="small text-muted">
                            {item.DATA ? new Date(item.DATA).toLocaleString() : ''}
                          </div>
                        </div>
                      </button>
                    ))}
                    {(!logsMov || logsMov.length === 0) && (
                      <div className="text-muted">Sem movimentações para este pedido.</div>
                    )}
                  </div>
                </Card.Body>
              </div>
            </Collapse>
          </Card>
        </Container>

        {/* Modal de Retirada */}
        <Modal show={showModal} onHide={handleCloseModal} centered>
          <Modal.Header closeButton className="custom-modal-header">
            <Modal.Title>Retirar Item</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-2 fw-semibold">{selectedItem?.DESCRICAO}</div>

            <div className="text-muted mb-3">
              <span className="me-3"><strong>SKU:</strong> {selectedItem?.CODIGO}</span>
              <span className="me-3">
                <strong>Já retirado (BD):</strong> {Number(selectedItem?.QUANTIDADE_RETIRADA || 0)}
              </span>
              <span className="me-3">
                <strong>Pendente (sessão):</strong> {selectedItem ? selectedQtyOf(selectedItem) : 0}
              </span>
              <span className="me-3">
                <strong>Saldo disp.:</strong> {selectedItem ? saldoDisponivelOf(selectedItem) : 0}
              </span>
            </div>

            <Form.Group className="mb-3" controlId="qty">
              <Form.Label>Quantidade</Form.Label>
              <InputGroup>
                <Button variant="outline-secondary" onClick={() => changeQty(-1)} disabled={saldoDisponivelOf(selectedItem || {}) <= 0}><Minus size={16} /></Button>
                <Form.Control
                  type="number"
                  min={0}
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  disabled={saldoDisponivelOf(selectedItem || {}) <= 0}
                />
                <Button variant="outline-secondary" onClick={() => changeQty(1)} disabled={saldoDisponivelOf(selectedItem || {}) <= 0}><Plus size={16} /></Button>
              </InputGroup>
              {saldoDisponivelOf(selectedItem || {}) <= 0 && (
                <div className="small text-danger mt-1">Sem saldo disponível para novas retiradas.</div>
              )}
            </Form.Group>

            <Form.Group controlId="obs">
              <Form.Label>Observações</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={obs}
                onChange={(e) => setObs(e.target.value)}
                placeholder="Opcional"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>Fechar</Button>
            <Button variant="primary" onClick={addWithdraw} disabled={saldoDisponivelOf(selectedItem || {}) <= 0}>Adicionar</Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  )
}
